import { useEffect, useState } from 'react';
import { lookupsAPI } from '../services/api';
import { LookupItem } from '../types';

const CACHE_TTL_MS = 0; // Always refresh on mount while sharing in-flight requests

let cachedDirections: LookupItem[] | null = null;
let cacheTimestamp = 0;
let pendingRequest: Promise<LookupItem[]> | null = null;

export function useTransactionDirections() {
  const [directions, setDirections] = useState<LookupItem[]>(cachedDirections ?? []);
  const [loading, setLoading] = useState<boolean>(!cachedDirections);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    if (cachedDirections) {
      setDirections(cachedDirections);
      setLoading(false);
    }

    const cacheIsStale = !cachedDirections || (Date.now() - cacheTimestamp) >= CACHE_TTL_MS;

    if (!cacheIsStale && cachedDirections) {
      return () => {
        isActive = false;
      };
    }

    if (!pendingRequest) {
      pendingRequest = lookupsAPI.getTransactionDirections()
        .then((result) => {
          cachedDirections = result;
          cacheTimestamp = Date.now();
          pendingRequest = null;
          return result;
        })
        .catch((requestError) => {
          pendingRequest = null;
          throw requestError;
        });
    }

    pendingRequest
      ?.then((result) => {
        if (!isActive) {
          return;
        }
        setDirections(result);
        setLoading(false);
      })
      .catch((requestError) => {
        if (!isActive) {
          return;
        }
        const normalizedError = requestError instanceof Error
          ? requestError
          : new Error('Failed to load transaction directions');
        setError(normalizedError);
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  return { directions, loading, error };
}
